import { Gift, Sparkles, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

interface LoyaltyProgram {
  isEnabled: number;
  pointsPerCurrencyMinorUnit: number;
  redemptionValueMinorPerPoint: number;
  minRedemptionPoints: number;
  maxRedemptionPercentOfBooking: number;
}

interface ReferralProgram {
  isEnabled: number;
  referrerRewardMinor: number;
  refereeRewardMinor: number;
  minRefereeBookingMinor: number;
  expiresDays: number;
  currency: string;
}

/**
 * Combined settings page for Loyalty + Referrals + Gift cards + Packages
 * configuration. Compact form-driven controls — power-user surface, not a
 * marketing page.
 */
export function LoyaltyProgramSettings() {
  const [loyalty, setLoyalty] = useState<LoyaltyProgram | null>(null);
  const [referral, setReferral] = useState<ReferralProgram | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<'loyalty' | 'referral' | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [lp, rp] = await Promise.all([
          fetch('/api/loyalty/program', { credentials: 'include' }).then((r) => r.json()),
          fetch('/api/referrals/program', { credentials: 'include' }).then((r) => r.json()),
        ]);
        setLoyalty(lp.program ?? null);
        setReferral(rp.program ?? null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function saveLoyalty(patch: Record<string, unknown>) {
    setSaving('loyalty');
    const res = await fetch('/api/loyalty/program', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(patch),
    });
    const data = (await res.json()) as { program?: LoyaltyProgram };
    if (data.program) setLoyalty(data.program);
    setMessage(res.ok ? 'Loyalty saved.' : 'Save failed.');
    setSaving(null);
  }

  async function saveReferral(patch: Record<string, unknown>) {
    setSaving('referral');
    const res = await fetch('/api/referrals/program', {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(patch),
    });
    const data = (await res.json()) as { program?: ReferralProgram };
    if (data.program) setReferral(data.program);
    setMessage(res.ok ? 'Referral saved.' : 'Save failed.');
    setSaving(null);
  }

  if (loading) {
    return <div className="py-8 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-8 py-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Loyalty &amp; Rewards</h1>
        <p className="text-sm text-muted-foreground">
          Points, referrals, gift cards, and multi-stay packages. Configure each independently.
        </p>
      </header>

      {message && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {message}
        </div>
      )}

      {/* Loyalty */}
      <section className="space-y-4 rounded-lg border bg-card p-6">
        <header className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Loyalty points</h2>
        </header>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={loyalty?.isEnabled === 1}
            onChange={(e) => saveLoyalty({ isEnabled: e.target.checked })}
            disabled={saving === 'loyalty'}
          />
          <span>Enable loyalty points program</span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Points per minor unit spent"
            value={loyalty?.pointsPerCurrencyMinorUnit ?? 1}
            onCommit={(n) => saveLoyalty({ pointsPerCurrencyMinorUnit: n })}
            disabled={saving === 'loyalty'}
            min={1}
          />
          <Field
            label="Minor units per point on redemption"
            value={loyalty?.redemptionValueMinorPerPoint ?? 1}
            onCommit={(n) => saveLoyalty({ redemptionValueMinorPerPoint: n })}
            disabled={saving === 'loyalty'}
            min={1}
          />
          <Field
            label="Minimum points to redeem"
            value={loyalty?.minRedemptionPoints ?? 100}
            onCommit={(n) => saveLoyalty({ minRedemptionPoints: n })}
            disabled={saving === 'loyalty'}
            min={0}
          />
          <Field
            label="Max % of booking paid by points"
            value={loyalty?.maxRedemptionPercentOfBooking ?? 50}
            onCommit={(n) => saveLoyalty({ maxRedemptionPercentOfBooking: n })}
            disabled={saving === 'loyalty'}
            min={1}
            max={100}
          />
        </div>
      </section>

      {/* Referrals */}
      <section className="space-y-4 rounded-lg border bg-card p-6">
        <header className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Referrals</h2>
        </header>
        <label className="flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={referral?.isEnabled === 1}
            onChange={(e) => saveReferral({ isEnabled: e.target.checked })}
            disabled={saving === 'referral'}
          />
          <span>Enable customer referrals program</span>
        </label>
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Referrer reward (minor units)"
            value={referral?.referrerRewardMinor ?? 2000}
            onCommit={(n) => saveReferral({ referrerRewardMinor: n })}
            disabled={saving === 'referral'}
            min={0}
          />
          <Field
            label="Referee reward (minor units)"
            value={referral?.refereeRewardMinor ?? 1000}
            onCommit={(n) => saveReferral({ refereeRewardMinor: n })}
            disabled={saving === 'referral'}
            min={0}
          />
          <Field
            label="Minimum referee booking (minor units)"
            value={referral?.minRefereeBookingMinor ?? 0}
            onCommit={(n) => saveReferral({ minRefereeBookingMinor: n })}
            disabled={saving === 'referral'}
            min={0}
          />
          <Field
            label="Pending expires after (days)"
            value={referral?.expiresDays ?? 90}
            onCommit={(n) => saveReferral({ expiresDays: n })}
            disabled={saving === 'referral'}
            min={1}
          />
        </div>
      </section>

      {/* Gift cards + packages links */}
      <section className="space-y-2 rounded-lg border bg-card p-6">
        <header className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Gift cards &amp; packages</h2>
        </header>
        <p className="text-sm text-muted-foreground">
          Manage gift cards and multi-stay package offerings from their dedicated pages:
        </p>
        <ul className="list-disc pl-5 text-sm">
          <li>
            <a className="text-primary underline" href="/settings/gift-cards">
              /settings/gift-cards
            </a>{' '}
            — audit log, issue, void.
          </li>
          <li>
            <a className="text-primary underline" href="/settings/packages">
              /settings/packages
            </a>{' '}
            — CRUD on multi-stay package offerings.
          </li>
        </ul>
      </section>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: number;
  onCommit: (n: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
}

function Field({ label, value, onCommit, disabled, min, max }: FieldProps) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const parsed = Number(draft);
          if (Number.isFinite(parsed) && parsed !== value) onCommit(parsed);
        }}
        disabled={disabled}
        className="rounded-md border bg-background px-2 py-1"
      />
    </label>
  );
}
